import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Signal,
  TemplateRef,
  computed,
  inject,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { routerSegments } from '@consts/routes';
import { IntelligenceModels } from '@models/intelligence.model';
import { DatasetTableConfig, UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import {
  UserOrganizationResultCellContext,
  createUserOrganizationsResultTableConfig,
} from './user-organizations-result-table.util';

/**
 * Read-only Cmd+K table of every organization available to the user, with the
 * active organization highlighted. Sibling of the user-organizations MCP widget.
 */
@Component({
  selector: 'app-user-organizations-result',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, UiDatasetTableComponent],
  templateUrl: './user-organizations-result.component.html',
  styleUrls: ['./user-organizations-result.component.scss'],
})
export class UserOrganizationsResultComponent {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  protected readonly nameCellTemplate =
    viewChild<TemplateRef<UserOrganizationResultCellContext>>('nameCell');

  protected readonly response: Signal<IntelligenceModels.Organizations.UserOrganizationsViewResponse | null> =
    computed(() => {
      const result = this.resultsService.current();
      return result?.type === 'user-organizations'
        ? (result.data as IntelligenceModels.Organizations.UserOrganizationsViewResponse)
        : null;
    });

  protected readonly organizations: Signal<IntelligenceModels.Organizations.UserOrganization[]> =
    computed(() => this.response()?.organizations ?? []);

  protected readonly tableConfig: Signal<DatasetTableConfig<IntelligenceModels.Organizations.UserOrganization> | null> =
    computed(() => {
      const nameCellTemplate = this.nameCellTemplate();
      return nameCellTemplate ? createUserOrganizationsResultTableConfig({ nameCellTemplate }) : null;
    });

  public constructor() {
    this.resultsService.setHeaderActions([
      {
        icon: 'ui-icon-new-tab',
        tooltip: 'USER_ORGANIZATIONS_RESULT.OPEN_PAGE',
        onClick: () => this.openOrganizationsPage(),
      },
    ]);
    this.destroyRef.onDestroy(() => this.resultsService.setHeaderActions([]));
  }

  protected isActive(organization: IntelligenceModels.Organizations.UserOrganization): boolean {
    return organization.id === this.response()?.activeOrganization?.id;
  }

  /** Open the page in a NEW TAB so the current Cmd+K session stays open. */
  private openOrganizationsPage(): void {
    const tree = this.router.createUrlTree(['/', routerSegments.account, routerSegments.organizations]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
